import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import {
  useGetGroupsQuery,
  useInviteMemberMutation,
  useGetDesignationsQuery,
  useLazyCheckMemberExistQuery,
} from "../../infrastructure/api/membersGroupsApi";
import { useGetRolesQuery } from "@/features/roles-permissions/infrastructure/api/RolesApi";

interface Props {
  open: boolean;
  onOpenChange: (val: boolean) => void;
}

const NAME_DISALLOWED_CHARS = /[^a-zA-Z\s_-]/g;

export const InviteMemberDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation("membersGroups");
  const [step, setStep] = useState<"form" | "success">("form");
  const [invitedName, setInvitedName] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState<string>("");
  const [roleId, setRoleId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<
    { id: string; name: string }[]
  >([]);

  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const emailCheckRef = useRef(0);
  const phoneCheckRef = useRef(0);
  const { data: roles = [], isLoading: rolesLoading } = useGetRolesQuery(
    undefined,
    { skip: !open },
  );

  const [checkMemberExist] = useLazyCheckMemberExistQuery();
  useGetDesignationsQuery(undefined, { skip: !open });
  const { data: groups = [], isLoading: groupsLoading } = useGetGroupsQuery(
    undefined,
    { skip: !open },
  );
  const [inviteMember, { isLoading: inviting }] = useInviteMemberMutation();

  const addGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group && !selectedGroups.some((g) => g.id === groupId)) {
      setSelectedGroups((prev) => [
        ...prev,
        { id: group.id, name: group.name },
      ]);
    }
  };

  const removeGroup = (groupId: string) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const handleClose = () => {
    setStep("form");
    setName("");
    setEmail("");
    setPhone("");
    setRoleId("");
    setDesignationId("");
    setSelectedGroups([]);
    setInvitedName("");
    setInvitedEmail("");
    onOpenChange(false);
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value.replace(NAME_DISALLOWED_CHARS, ""));
  };

  const handleInvite = async () => {
    if (!isValid) return;

    try {
      const [emailCheck, phoneCheck] = await Promise.all([
        checkMemberExist({ email: email.trim() }).unwrap(),
        checkMemberExist({ phone }).unwrap(),
      ]);

      if (emailCheck.exists) {
        toast.error(
          emailCheck.message || t("inviteDialog.emailAlreadyExists"),
        );
        return;
      }

      if (phoneCheck.exists) {
        toast.error(
          phoneCheck.message || t("inviteDialog.phoneAlreadyExists"),
        );
        return;
      }

      await inviteMember({
        email: email.trim(),
        name: name.trim(),
        phone,
        role_id: roleId,
        ...(designationId ? { designation_id: designationId } : {}),
        group_ids: selectedGroups.length
          ? selectedGroups.map((g) => g.id)
          : undefined,
      }).unwrap();

      setInvitedName(name.trim());
      setInvitedEmail(email.trim());
      setStep("success");
    } catch {
      toast.error(t("inviteDialog.inviteFailed"));
    }
  };

  useEffect(() => {
    const trimmedEmail = email.trim();

    const requestId = emailCheckRef.current + 1;
    emailCheckRef.current = requestId;

    setEmailError("");

    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const response = await checkMemberExist({
          email: trimmedEmail,
        }).unwrap();

        if (emailCheckRef.current !== requestId) return;

        if (response.is_available === false) {
          setEmailError(
            response.message || t("inviteDialog.userEmailAlreadyExists"),
          );
        }
      } catch {
        // ignore validation failures
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [email, checkMemberExist]);

  useEffect(() => {
    const requestId = phoneCheckRef.current + 1;
    phoneCheckRef.current = requestId;

    setPhoneError("");

    if (!phone || !isValidPhoneNumber(phone)) {
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const response = await checkMemberExist({
          phone,
        }).unwrap();

        if (phoneCheckRef.current !== requestId) return;

        if (response.is_available === false) {
          setPhoneError(
            response.message || t("inviteDialog.userPhoneAlreadyExists"),
          );
        }
      } catch {
        // ignore validation failures
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [phone, checkMemberExist]);
  const isValid =
    name.trim() &&
    email.trim() &&
    phone &&
    roleId &&
    !emailError &&
    !phoneError;

  if (step === "success") {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="
    flex
    w-[95vw]
    max-w-[95vw]
    flex-col
    sm:max-w-[500px]
    p-0
    rounded-xl
    overflow-hidden
    border
  "
        >
          <div className="flex flex-col items-center gap-4 px-6 py-10 text-center sm:px-10">
            <CheckCircle2 className="h-14 w-14 text-green-400" />
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">
                {t("inviteDialog.successTitle")}
              </h2>
              <p className="text-sm text-gray-500">
                {invitedEmail
                  ? t("inviteDialog.successMessageWithEmail", {
                      name: invitedName || t("inviteDialog.theMember"),
                      email: invitedEmail,
                    })
                  : t("inviteDialog.successMessageNoEmail", {
                      name: invitedName || t("inviteDialog.theMember"),
                    })}
              </p>
            </div>
            <Button
              className="w-full bg-primary text-white hover:bg-primary cursor-pointer sm:w-auto"
              onClick={handleClose}
            >
              {t("inviteDialog.done")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="
    flex
    w-[95vw]
    max-w-[95vw]
    flex-col
    sm:max-w-[600px]
    md:max-w-[650px]
    lg:max-w-[700px]
    p-0
    rounded-xl
    overflow-hidden
    border
    max-h-[calc(100dvh-1rem)]
  "
      >
        {" "}
        {/* Header */}
        <DialogHeader
          className="
    flex
    flex-row
    items-center
    justify-between
    border-b
    px-4
    py-4
    sm:px-6
  "
        >
          {" "}
          <DialogTitle className="text-lg font-semibold">
            {t("inviteDialog.title")}
          </DialogTitle>
        </DialogHeader>
        {/* Body — scrollable so all fields fit */}
        <div
          className="
    min-h-0
    flex-1
    px-4
    sm:px-6
    py-4
    space-y-5
    overflow-y-auto
  "
        >
          {" "}
          {/* Name */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("inviteDialog.nameLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Input
              name="invite-member-name"
              autoComplete="off"
              placeholder={t("inviteDialog.namePlaceholder")}
              value={name}
              onChange={handleNameChange}
            />
          </div>
          {/* Email */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("inviteDialog.emailLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Input
              name="invite-member-email"
              autoComplete="off"
              placeholder={t("inviteDialog.emailPlaceholder")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {emailError && (
              <p className="text-sm text-destructive-text">{emailError}</p>
            )}
          </div>
          {/* Mobile */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("inviteDialog.phoneLabel")} <span className="text-destructive-text">*</span>
            </label>
            <div className="flex h-10 rounded-md border border-input overflow-hidden">
              <PhoneInput
                defaultCountry="IN"
                international
                countryCallingCodeEditable={false}
                placeholder={t("inviteDialog.phonePlaceholder")}
                value={phone}
                onChange={(val) => setPhone(val ?? "")}
              />
            </div>
            {phoneError && (
              <p className="text-sm text-destructive-text">{phoneError}</p>
            )}
          </div>
          {/* Role */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("inviteDialog.roleLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Select
              value={roleId}
              onValueChange={setRoleId}
              disabled={rolesLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={rolesLoading ? t("inviteDialog.loading") : t("inviteDialog.selectRole")}
                />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Designation */}
          {/* <div className="space-y-1">
            <label className="text-sm font-medium">
              Designation <span className="text-destructive-text">*</span>
            </label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={designationId}
              onChange={(e) => setDesignationId(e.target.value)}
              disabled={designationsLoading}
            >
              <option value="">
                {designationsLoading ? "Loading..." : "Select Designation"}
              </option>
              {designations.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div> */}
          {/* Groups (multi-select) */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t("inviteDialog.groupsLabel")}</label>
            <Select value="" onValueChange={addGroup} disabled={groupsLoading}>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={groupsLoading ? t("inviteDialog.loading") : t("inviteDialog.addToGroup")}
                />
              </SelectTrigger>
              <SelectContent>
                {groups
                  .filter((g) => !selectedGroups.some((s) => s.id === g.id))
                  .map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {selectedGroups.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 max-w-full">
                {" "}
                {selectedGroups.map((group) => (
                  <div
                    key={group.id}
                    className="
    flex
    items-center
    gap-1
    px-2
    py-1
    bg-gray-100
    rounded-md
    text-sm
    max-w-full
    break-all
  "
                  >
                    {group.name}
                    <button onClick={() => removeGroup(group.id)}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Footer */}
        <DialogFooter
          className="
    -mx-0
    mb-0
    shrink-0
    border-t
    px-4
    pt-4
    pb-6
    sm:px-6
    sm:pb-7
    flex
    flex-col-reverse
    sm:flex-row
    sm:justify-end
    gap-3
  "
        >
          {" "}
          <Button
            variant="outline"
            className="
    w-full
    sm:w-auto
    border-primary
    text-primary
    cursor-pointer
    hover:bg-white
    hover:text-primary
  "
            onClick={handleClose}
            disabled={inviting}
          >
            {t("inviteDialog.cancel")}
          </Button>
          <Button
            className="
    w-full
    sm:w-auto
    bg-primary
    text-white
    hover:bg-primary
    cursor-pointer
  "
            onClick={handleInvite}
            disabled={!isValid}
            loading={inviting}
          >
            {t("inviteDialog.invite")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
